import { cloneLibrary } from "../domain/library";
import { emptyLibrary, type Library } from "../domain/types";
import { failReasonOf, type FailReason } from "../storage/errors";
import type { LibraryRepository, LoadResult } from "../storage/repository";

/** shown wherever a save fails; the person's input and the previous data are kept */
export const SAVE_FAILED_MESSAGE = "บันทึกไม่ได้ พื้นที่เก็บข้อมูลของเบราว์เซอร์เต็มหรือถูกปฏิเสธ ข้อมูลเดิมยังอยู่ ลองอีกครั้งได้";
export const FAIL_MESSAGES: Record<FailReason, string> = {
  storage: SAVE_FAILED_MESSAGE,
  offline: "บันทึกไม่สำเร็จ เชื่อมต่อ Cloud ไม่ได้ (เน็ตหลุดหรือช้า) ข้อมูลเดิมและค่าที่กรอกยังอยู่ ลองอีกครั้งได้",
  session: "บันทึกไม่สำเร็จ เซสชันหมดอายุ เข้าสู่ระบบอีกครั้งแล้วกดบันทึกใหม่ ค่าที่กรอกยังอยู่",
  conflict: "ยังไม่ได้บันทึก ข้อมูลนี้ถูกเปลี่ยนจากอีกเครื่องหลังจากที่เปิดไว้ โหลดข้อมูลล่าสุดก่อนแล้วลองอีกครั้ง ค่าที่กรอกยังอยู่",
  account: "บัญชีเปลี่ยนแล้ว คำขอนี้ถูกยกเลิก",
  server: "บันทึกไม่สำเร็จ Cloud ไม่รับข้อมูลนี้ ข้อมูลเดิมยังอยู่"
};

export type SaveFail = { ok: false; reason: FailReason; message: string };
export type SaveResult = { ok: true } | SaveFail;
const fail = (reason: FailReason): SaveFail => ({ ok: false, reason, message: FAIL_MESSAGES[reason] });

/**
 * Holds the current library and saves it after every change.
 * Every change is prepared on a copy and only becomes the app's state after the save succeeds,
 * so a failed save (storage full, offline, session expired, conflict) leaves the previous data exactly as it was.
 * Saves run one at a time, in order. After dispose() (log out / switch account) nothing is applied any more.
 * UI code calls commit(lib => mutate(lib)); it never touches storage directly.
 */
export class LibraryStore {
  lib: Library = emptyLibrary();
  storageOK = true;
  lastSaved: string | null = null;
  /** false until the person has made a first-run choice (or data was found) */
  started = false;
  loadIssue: string | null = null;
  disposed = false;
  private queue: Promise<unknown> = Promise.resolve();

  constructor(readonly repo: LibraryRepository) {}

  async init(): Promise<LoadResult> {
    const r = await this.repo.load();
    if (this.disposed) return { status: "error", error: FAIL_MESSAGES.account };
    if (r.status === "ok") { this.lib = r.library; this.started = true; }
    if (r.status === "error") { this.loadIssue = r.error; this.storageOK = !/ไม่อนุญาต/.test(r.error); }
    return r;
  }

  /** Applies mutate to a copy, saves the copy, then swaps it in. On failure the state is unchanged. */
  commit(mutate: (lib: Library) => void): Promise<SaveResult> {
    return this.serial(() => { const draft = cloneLibrary(this.lib); mutate(draft); return this.saveThenApply(draft); });
  }

  /** Saves next, then makes it the current library. On failure the state is unchanged. */
  replace(next: Library): Promise<SaveResult> {
    return this.serial(() => this.saveThenApply(cloneLibrary(next)));
  }

  /** Loads the latest saved copy (e.g. after a conflict). Keeps the current state if loading fails. */
  reload(): Promise<SaveResult> {
    return this.serial(async () => {
      const r = await this.repo.load();
      if (this.disposed) return fail("account");
      if (r.status === "error") return { ok: false, reason: "offline", message: r.error } as SaveFail;
      if (r.status === "ok") { this.lib = r.library; this.started = true; }
      else { this.lib = emptyLibrary(); this.started = false; }
      return { ok: true } as SaveResult;
    });
  }

  /** Deletes everything in the repository, then shows an empty library. */
  clearAll(): Promise<SaveResult> {
    return this.serial(async () => {
      try { await this.repo.clear(); } catch (e) { return fail(this.disposed ? "account" : failReasonOf(e)); }
      if (this.disposed) return fail("account");
      this.lib = emptyLibrary(); this.started = false;
      return { ok: true } as SaveResult;
    });
  }

  dispose() { this.disposed = true; this.repo.dispose?.(); }

  private serial<T>(run: () => Promise<T>): Promise<T> {
    const next = this.queue.then(run, run);
    this.queue = next.catch(() => undefined);
    return next;
  }

  private async saveThenApply(draft: Library): Promise<SaveResult> {
    if (this.disposed) return fail("account");
    try {
      await this.repo.save(draft);
    } catch (e) {
      if (this.disposed) return fail("account");
      const reason = failReasonOf(e);
      if (reason === "storage") this.storageOK = false;
      return fail(reason);
    }
    if (this.disposed) return fail("account");
    this.lib = draft;
    this.started = true;
    this.storageOK = true;
    this.lastSaved = new Date().toISOString();
    return { ok: true };
  }
}
